import { useEffect, useRef, useState } from "react";
import Input from "@Components/Input";
import styles from "../../../LeadFormModal/LeadFormModal.module.scss";
import { fetchUniversities } from "services/regionsHelper";
import DropdownIcon from "@Icons/DropdownIcon";
import useClickOutside from "hooks/useOutsideClick";
import { shallowEqual, useSelector } from "react-redux";
import { AppState } from "@Store/index";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import { logger } from "helpers/logger";
import LoaderIcon from "@Icons/LoaderIcon";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";

const UniversityDropdown = ({
  error,
  setErrors,
  selectedUniversity,
  setSelectedUniversity,
  testIdPrefix,
  creatable = false,
  required = true,
  isBookForm = false,
  showPlaceholderAndLabel = false,
  label,
  placeholder,
  isSearchLeadFormSection = false,
}: any) => {
  const [inpValue, setInpValue] = useState(selectedUniversity);
  const [universities, setUniversities] = useState<any>([]);
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const ref = useRef(null);
  const customUniversity: any = useRef("");

  const { inventory, region, filteredUniversity } = useSelector(
    ({ leadFormModal, searchPageData, bookFormData }: AppState) => ({
      inventory: bookFormData.data || leadFormModal.inventory,
      region: searchPageData.region,
      filteredUniversity: searchPageData.filters.university,
    }),
    shallowEqual,
  );

  const handleSelectUniversity = (university: any) => {
    if (customUniversity.current !== university.name) {
      localStorage.removeItem("custom-university");
      customUniversity.current = "";
      getUniversities();
    }
    setInpValue(university.name);
    setSelectedUniversity(university.name);
    setErrors((errors: any) => ({ ...errors, university: "" }));
    setTimeout(() => setShowOptions(false), 100);
  };

  const getUniversities = async (searchName = "") => {
    setLoading(true);
    let options: Record<string, string | number> = {
      state: "active",
      sort_key: "name",
      sort_order: "asc",
      region_types: "establishment",
      only: "name,secondary_name",
      limit: 30,
      ancestor_id: region?.id || inventory?.region_id,
    };

    if (isBookForm) {
      options = {
        limit: 30,
        sort_key: "distance",
        sort_order: "asc",
        region_point_sort: [
          inventory?.location?.location_coordinates?.lat,
          inventory?.location?.location_coordinates?.lng,
        ].join(", "),
        region_type: "establishment",
        only: "name,secondary_name",
        region_inventory_id_point: inventory?.id,
      };
    }

    if (searchName) {
      delete options.ancestor_id;
      options.search_name = searchName;
    }

    if (isBookForm && selectedUniversity) {
      options.search_name = selectedUniversity;
    }

    const response = await fetchUniversities(options);
    if (response.message === "success") {
      let data = response?.data?.result;

      if (customUniversity.current) {
        data = [{ name: customUniversity.current, secondary_name: "" }, ...data];
      }

      if (selectedUniversity && isBookForm) {
        const selectedUniversityValue = data.find(
          (university: any) => university.name === selectedUniversity,
        );
        if (selectedUniversityValue) {
          data = [
            selectedUniversityValue,
            ...data.filter((university: any) => university.name !== selectedUniversity),
          ];
        }
      }
      setUniversities(data);
    }
    setLoading(false);
  };

  const fetchData = async (searchName: string) => {
    await getUniversities(searchName);
  };

  const debounce = (func: any, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    // eslint-disable-next-line func-names
    return function (...args: any[]) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        // eslint-disable-next-line prefer-spread
        func.apply(null, args);
      }, delay);
    };
  };

  const debouncedFetchData = debounce(fetchData, 1000);

  const handleInputChange = (value: string) => {
    setIsTyping(true);
    setInpValue(value);
    debouncedFetchData(value);
    setTimeout(() => setIsTyping(false), 100);
  };

  useEffect(() => {
    if (filteredUniversity && filteredUniversity === region.canonical_name) {
      getUniversities(region.name);
      setSelectedUniversity(region.name);
    } else {
      getUniversities();
    }
  }, [filteredUniversity]);

  useClickOutside(ref, () => {
    if (showOptions) {
      const isSelectedValuePresent = universities.find(
        (opt: any) => opt.name === selectedUniversity,
      );
      if (inpValue !== selectedUniversity || !selectedUniversity) {
        if (!isSelectedValuePresent?.name && !customUniversity.current) {
          setInpValue("");
          if (universities.length === 0) getUniversities();
        }
      }
      if (isSelectedValuePresent?.name) {
        if (!customUniversity.current) {
          setInpValue(isSelectedValuePresent.name);
        }
      }
      setTimeout(() => setShowOptions(false), 0);
    }
  });

  const handleCreateOption = (e: any) => {
    e.preventDefault();
    setInpValue(inpValue);
    setSelectedUniversity(inpValue);
    customUniversity.current = inpValue;
    try {
      localStorage.setItem("custom-university", inpValue);
      localStorage.setItem("custom-university-inventory", inventory?.id || "");
    } catch (err) {
      logger.error(err);
    }
    setErrors((errors: any) => ({ ...errors, university: "" }));
    setTimeout(() => setShowOptions(false), 0);
  };

  const handleClear = () => {
    try {
      setSelectedUniversity("");
      setInpValue("");
      setShowOptions(false);
      localStorage.removeItem("custom-university-inventory");
      if (customUniversity.current) {
        getUniversities();
      }
    } catch (err) {
      logger.error(err);
    }
  };

  const handleOnFocus = () => {
    setShowOptions(true);
    if (universities.length === 0) {
      getUniversities();
    }
    if (
      customUniversity.current &&
      !universities.find((uni: any) => uni.name === customUniversity.current)
    ) {
      setUniversities((data: any) => [
        { name: customUniversity.current, secondary_name: "" },
        ...data,
      ]);
    }
  };

  useEffect(() => {
    customUniversity.current =
      typeof localStorage !== "undefined" && localStorage.getItem("custom-university");

    try {
      const customUniversityInventory = localStorage.getItem("custom-university-inventory");

      if (
        customUniversity.current &&
        !selectedUniversity &&
        customUniversityInventory === inventory?.id
      ) {
        setInpValue(customUniversity.current);
        setSelectedUniversity(customUniversity.current);
      }
    } catch (err) {
      logger.error(err);
    }
  }, [selectedUniversity, inventory?.id]);

  useEffect(() => {
    if (selectedUniversity && !inpValue && !isTyping) {
      setInpValue(selectedUniversity);
    }
  }, [selectedUniversity, inpValue, isTyping]);

  // Clear university input when inventory changes
  useEffect(() => {
    if (inventory?.id) {
      setInpValue("");
      setSelectedUniversity("");
      setUniversities([]);
    }
  }, [inventory?.id]);
  const { t } = useTranslationHook();

  return (
    <div className={styles.universityDropdownContainer} ref={ref}>
      <Input
        type="dropdown"
        label={label || t("University")}
        placeholder={placeholder || t("Enter University")}
        value={inpValue}
        onFocus={handleOnFocus}
        onChange={handleInputChange}
        handleClear={handleClear}
        clearable
        rightIconClassName={error ? styles.rightIconWithError : ""}
        rightIcon={!selectedUniversity && <DropdownIcon size={15} />}
        showErrorMessage={Boolean(error)}
        error={error}
        dataTestId={`${testIdPrefix || ""}University-input`}
        required={required}
        id="universityAccomodation"
        showPlaceholderAndLabel={showPlaceholderAndLabel}
        isSearchLeadFormSection={isSearchLeadFormSection}
      />
      {showOptions && universities.length === 0 && loading && (
        <div className={formatClassNames(styles.universityDropdownOptContainer, styles.loader)}>
          <LoaderIcon fill="#e53863" size={20} />
        </div>
      )}
      {showOptions && creatable && inpValue?.length > 3 && inpValue !== selectedUniversity && (
        <div className={formatClassNames(styles.creatable_options_container)}>
          <div
            className={formatClassNames(styles.option, styles.creatable)}
            onClick={handleCreateOption}
            role="none"
          >
            <div>{`+ Add ${inpValue}`}</div>
          </div>
        </div>
      )}
      {!loading && showOptions && universities.length === 0 && !creatable && (
        <div className={formatClassNames(styles.universityDropdownOptContainer, styles.no_results)}>
          No Results
        </div>
      )}
      {showOptions && universities.length > 0 && (
        <div className={styles.universityDropdownOptContainer}>
          {universities.map((university: any, index: number) => (
            <div
              key={`${university.name}_${university.secondary_name}_${index}`}
              className={formatClassNames(
                styles.option,
                university.name === inpValue ? styles.selected : "",
              )}
              onClick={() => handleSelectUniversity(university)}
              data-testid={`${testIdPrefix || ""}leadform-universityNames`}
              role="none"
            >
              <div>{university.name}</div>
              <div className={styles.optionDesc}>{university.secondary_name}</div>
            </div>
          ))}
          {creatable && inpValue?.length > 3 && inpValue !== selectedUniversity && (
            <div
              className={formatClassNames(styles.option, styles.creatable)}
              onClick={handleCreateOption}
              role="none"
            >
              <div>{`+ Add ${inpValue}`}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default wrapperHOC(UniversityDropdown, {
  componentName: "UniversityDropdown-dropdown-LeadForm-Global",
  showForChina: true,
});
